import { useCallback, useEffect, useMemo, useState } from "react";
import {
  IoCheckmarkDoneOutline,
  IoEyeOutline,
  IoTrashOutline,
} from "react-icons/io5";
import {
  deleteHostingOrder,
  listHostingOrders,
  markHostingOrderRead,
} from "../../../api/adminHostingOrdersApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import ConfirmDialog from "../../../components/dashboard/ConfirmDialog";
import EmptyState from "../../../components/dashboard/EmptyState";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";
import { useNotify } from "../../../components/ui/NotificationProvider";

const PLAN_LABELS = {
  orbit: "Orbit",
  nebula: "Nebula",
  galaxy: "Galaxy",
};

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function labelFor(map, value) {
  return map[value] || value || "—";
}

function OrderDetailModal({ order, busy, onClose, onMarkRead, onDelete }) {
  if (!order) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
      />
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              {order.firstName} {order.lastName}
            </h2>
            <p className="mt-1 text-sm text-slate-500">Hosting order</p>
          </div>
          {!order.isRead ? (
            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
              New
            </span>
          ) : null}
        </div>

        <dl className="mt-6 grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="font-medium text-slate-500">Email</dt>
            <dd className="mt-1 text-slate-900">{order.email}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Phone</dt>
            <dd className="mt-1 text-slate-900">{order.phone || "—"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Company</dt>
            <dd className="mt-1 text-slate-900">{order.company || "—"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Hosting plan</dt>
            <dd className="mt-1 text-slate-900">
              {labelFor(PLAN_LABELS, order.plan)}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Domain</dt>
            <dd className="mt-1 text-slate-900">{order.domain || "—"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Submitted</dt>
            <dd className="mt-1 text-slate-900">{formatDate(order.createdAt)}</dd>
          </div>
        </dl>

        {order.description ? (
          <div className="mt-6">
            <p className="text-sm font-medium text-slate-500">Project description</p>
            <p className="mt-2 whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm text-slate-800">
              {order.description}
            </p>
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap gap-2">
          {!order.isRead ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onMarkRead(order, true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              <IoCheckmarkDoneOutline className="h-4 w-4" />
              Mark as read
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => onMarkRead(order, false)}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
            >
              Mark as unread
            </button>
          )}

          <button
            type="button"
            disabled={busy}
            onClick={() => onDelete(order)}
            className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            <IoTrashOutline className="h-4 w-4" />
            Delete
          </button>

          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-slate-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function HostingOrdersManager() {
  const notify = useNotify();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listHostingOrders();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((item) => {
      if (filter === "unread" && item.isRead) return false;
      if (filter === "read" && !item.isRead) return false;
      if (!q) return true;

      const haystack = [
        item.firstName,
        item.lastName,
        item.email,
        item.phone,
        item.company,
        item.plan,
        item.domain,
        item.description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [orders, query, filter]);

  async function handleMarkRead(order, isRead) {
    setBusy(true);
    try {
      const updated = await markHostingOrderRead(order.id, isRead);
      setOrders((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item))
      );
      setSelected((prev) => (prev?.id === updated.id ? updated : prev));
      notify.success(isRead ? "Marked as read" : "Marked as unread");
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function openOrder(order) {
    setSelected(order);
    if (!order.isRead) {
      try {
        const updated = await markHostingOrderRead(order.id, true);
        setOrders((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item))
        );
        setSelected(updated);
      } catch {
        // Keep modal open even if mark-read fails.
      }
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteHostingOrder(deleteTarget.id);
      setOrders((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      if (selected?.id === deleteTarget.id) setSelected(null);
      notify.success("Hosting order deleted");
      setDeleteTarget(null);
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingState message="Loading hosting orders…" />;
  if (error) return <ErrorState message={error} onRetry={loadOrders} />;

  const unreadCount = orders.filter((o) => !o.isRead).length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Hosting orders</h2>
        <p className="mt-1 text-sm text-slate-500">
          {orders.length} total
          {unreadCount > 0 ? ` · ${unreadCount} unread` : ""}
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, plan, or domain…"
          className="w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
        />

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-400"
        >
          <option value="all">All</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title={query || filter !== "all" ? "No matching orders" : "No hosting orders yet"}
          description={
            query || filter !== "all"
              ? "Try a different search or filter."
              : "Hosting orders submitted from the website will appear here."
          }
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">
                  Customer
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 md:table-cell">
                  Plan
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 lg:table-cell">
                  Domain
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 sm:table-cell">
                  Submitted
                </th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((item) => (
                <tr
                  key={item.id}
                  className={`hover:bg-slate-50/80 ${!item.isRead ? "bg-emerald-50/30" : ""}`}
                >
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-900">
                        {item.firstName} {item.lastName}
                        {!item.isRead ? (
                          <span className="ml-2 inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                            New
                          </span>
                        ) : null}
                      </p>
                      <p className="truncate text-xs text-slate-500">{item.email}</p>
                    </div>
                  </td>
                  <td className="hidden px-4 py-3 text-slate-600 md:table-cell">
                    {labelFor(PLAN_LABELS, item.plan)}
                  </td>
                  <td className="hidden max-w-xs truncate px-4 py-3 text-slate-600 lg:table-cell">
                    {item.domain || "—"}
                  </td>
                  <td className="hidden px-4 py-3 text-slate-600 sm:table-cell">
                    {formatDate(item.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openOrder(item)}
                        className="rounded-lg border border-gray-200 p-2 text-slate-600 hover:bg-gray-50"
                        aria-label={`View hosting order from ${item.firstName} ${item.lastName}`}
                      >
                        <IoEyeOutline className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(item)}
                        className="rounded-lg border border-gray-200 p-2 text-red-600 hover:bg-red-50"
                        aria-label={`Delete hosting order from ${item.firstName} ${item.lastName}`}
                      >
                        <IoTrashOutline className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <OrderDetailModal
        order={selected}
        busy={busy}
        onClose={() => setSelected(null)}
        onMarkRead={handleMarkRead}
        onDelete={(item) => {
          setSelected(null);
          setDeleteTarget(item);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete hosting order?"
        message={
          deleteTarget
            ? `This will permanently delete the hosting order from ${deleteTarget.firstName} ${deleteTarget.lastName}.`
            : ""
        }
        busy={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
